import React, { useState, useEffect, useMemo, Fragment, useCallback, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  BriefcaseIcon,
  FolderIcon,
  TagIcon,
  CalendarDaysIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { format, parseISO, isPast, isToday as dateFnsIsToday } from 'date-fns';
import TaskForm from './forms/TaskForm';
import { apiClient } from '../api/apiConfig';
import { Task } from '../types/task';
import { Category } from '../types/category';
import { Project } from '../types/project';
import { Business } from '../types/business';
import { formatISOForInput } from '../utils/dateUtils';
import { Button, Alert, Spinner, Badge } from './ui';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

// Fallback Data
const fallbackTasks: Task[] = [
    {
      id: 1,
      description: "Review project proposal",
      is_done: false,
      due_date: "2024-08-15T10:00:00Z",
      created_at: "2024-08-01T09:00:00Z",
      completion_date: null,
      importance: 'should_do',
      is_urgent: false,
      top_priority: false,
      business: { id: 1, name: "Acme Corp" },
      project: { id: 1, name: "Website Redesign" },
      categories: [{ id: 1, name: "Planning" }],
      responsible: [],
      impacted: []
    },
    {
      id: 2,
      description: "Develop login feature",
      is_done: true,
      due_date: "2024-08-10T17:00:00Z",
      created_at: "2024-07-25T14:30:00Z",
      completion_date: "2024-08-10T17:00:00Z",
      importance: 'must_do',
      is_urgent: true,
      top_priority: false,
      business: null,
      project: { id: 2, name: "Mobile App Dev" },
      categories: [{ id: 2, name: "Development" }, { id: 3, name: "Frontend" }],
      responsible: [],
      impacted: []
    },
    {
      id: 3,
      description: "Schedule team meeting",
      is_done: false,
      due_date: null,
      created_at: "2024-08-05T11:00:00Z",
      completion_date: null,
      importance: 'could_do',
      is_urgent: false,
      top_priority: false,
      business: null,
      project: null,
      categories: [],
      responsible: [],
      impacted: []
    },
    {
      id: 4,
      description: "Prepare tax documents",
      is_done: false,
      top_priority: false,
      due_date: "2024-08-25T17:00:00Z",
      created_at: "2024-08-06T10:00:00Z",
      completion_date: null,
      importance: 'should_do',
      is_urgent: false,
      business: { id: 2, name: "Personal Finance"},
      project: null,
      categories: [{ id: 4, name: "Finance" }],
      responsible: [],
      impacted: []
    },
];
const fallbackCategories: Category[] = [ {id: 1, name: "Planning"}, {id: 2, name: "Development"}, {id: 3, name: "Frontend"}, {id: 4, name: "Finance"} ];
const fallbackProjects: Project[] = [ {id: 1, name: "Website Redesign"}, {id: 2, name: "Mobile App Dev"} ];

type SortField = 'due_date' | 'created_at' | 'business';
type SortDirection = 'asc' | 'desc';
type StatusFilter = 'all' | 'active' | 'completed';

interface TaskListProps {
  showCompletedByDefault?: boolean;
}

const TaskList: React.FC<TaskListProps> = ({ showCompletedByDefault = true }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(showCompletedByDefault ? 'all' : 'active');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [businessFilter, setBusinessFilter] = useState<string>('all');
  const [showDueTodayOrBefore, setShowDueTodayOrBefore] = useState<boolean>(false);
  const [showToPlan, setShowToPlan] = useState<boolean>(false);
  const [showTopPriority, setShowTopPriority] = useState<boolean>(false);

  // Sorting
  const [sortBy, setSortBy] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Delete State
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [taskToDeleteId, setTaskToDeleteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Edit State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null);

  // Toggle Status State
  const [taskBeingToggledId, setTaskBeingToggledId] = useState<number | null>(null);
  const [isToggling, setIsToggling] = useState(false);

  // --- Inline Date Edit State ---
  const [editingDateTaskId, setEditingDateTaskId] = useState<number | null>(null);
  const [isUpdatingDate, setIsUpdatingDate] = useState(false);
  const dateInputRef = useRef<HTMLInputElement>(null); // Ref for focusing
  // ------------------------------

  // --- Inline Project Edit State ---
  const [editingProjectTaskId, setEditingProjectTaskId] = useState<number | null>(null);
  const [isUpdatingProject, setIsUpdatingProject] = useState(false);
  const projectSelectRef = useRef<HTMLSelectElement>(null);
  // ------------------------------

  // --- Inline Business Edit State ---
  const [editingBusinessTaskId, setEditingBusinessTaskId] = useState<number | null>(null);
  const [isUpdatingBusiness, setIsUpdatingBusiness] = useState(false);
  const businessSelectRef = useRef<HTMLSelectElement>(null);
  // ------------------------------

  // --- Inline Categories Edit State ---
  const [editingCategoriesTaskId, setEditingCategoriesTaskId] = useState<number | null>(null);
  const [isUpdatingCategories, setIsUpdatingCategories] = useState(false);
  const [tempCategories, setTempCategories] = useState<number[]>([]);
  // ------------------------------

  // Hover states for elements that need hover effects
  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null);
  const [hoveredSortField, setHoveredSortField] = useState<SortField | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [tasksRes, categoriesRes, projectsRes, businessesRes] = await Promise.all([
          apiClient.get('/api/tasks/').catch(() => ({ data: fallbackTasks })),
          apiClient.get('/api/categories/?type=general').catch(() => ({ data: fallbackCategories })),
          apiClient.get('/api/projects/').catch(() => ({ data: fallbackProjects })),
          apiClient.get('/api/businesses/').catch(() => ({ data: [] }))
        ]);
        setTasks(tasksRes.data);
        setCategories(categoriesRes.data);
        setProjects(projectsRes.data);
        setBusinesses(businessesRes.data);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data. Displaying sample data.");
        setTasks(fallbackTasks);
        setCategories(fallbackCategories);
        setProjects(fallbackProjects);
        setBusinesses([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleToggleDone = async (taskId: number) => {
    const originalTasks = [...tasks];
    const taskToToggle = originalTasks.find(t => t.id === taskId);
    if (!taskToToggle) return;

    setTaskBeingToggledId(taskId); // Indicate which task is being toggled
    setIsToggling(true);

    // Optimistically update UI - NOW includes completion_date
    const newIsDoneStatus = !taskToToggle.is_done;
    setTasks(currentTasks =>
      currentTasks.map(task => {
        if (task.id === taskId) {
            return {
                ...task,
                is_done: newIsDoneStatus,
                // Set/clear completion_date locally for immediate UI update
                completion_date: newIsDoneStatus ? new Date().toISOString() : null
            };
        }
        return task;
      })
    );

    try {
      // Actual API call to update the task status
      await apiClient.patch(`/api/tasks/${taskId}/`, {
        is_done: newIsDoneStatus
      });

      // Success! Keep the optimistic update.
    } catch (error: any) {
      console.error("Failed to update task status:", error);
      // Revert UI on error
      setTasks(originalTasks);
      // Show error message to user
      alert(`Error updating task: ${error.message || 'Failed to update status.'}`);
    } finally {
      setIsToggling(false);
      setTaskBeingToggledId(null); // Clear indicator
    }
  };

  const openConfirmationModal = (taskId: number) => {
    setTaskToDeleteId(taskId);
    setDeleteError(null); // Clear previous errors
    setIsConfirmModalOpen(true);
  };

  const closeConfirmationModal = useCallback(() => {
    if (isDeleting) return; // Prevent closing while delete is in progress
    setIsConfirmModalOpen(false);
    // Delay resetting ID to allow modal fade out
    setTimeout(() => {
        setTaskToDeleteId(null);
        setDeleteError(null);
    }, 300);
  }, [isDeleting]);

  const handleDeleteTask = async () => {
    if (taskToDeleteId === null) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await apiClient.delete(`/api/tasks/${taskToDeleteId}/`);

      // Remove task from local state on success
      setTasks(currentTasks => currentTasks.filter(task => task.id !== taskToDeleteId));
      closeConfirmationModal();
      // TODO: Add success toast notification if desired
    } catch (err: any) {
      console.error("Error deleting task:", err);
      const errorMsg = err.message || 'Failed to delete task.';
      setDeleteError(errorMsg);
      // Keep modal open to show error
    } finally {
      setIsDeleting(false);
    }
  };

  const openEditModal = (task: Task) => {
    setTaskToEdit(task);
    // Clear previous errors if reusing form
    // setEditError(null);
    setIsEditModalOpen(true);
  };

  const closeEditModal = useCallback(() => {
    // Add check if submitting edit later
    // if (isEditing) return;
    setIsEditModalOpen(false);
    // Delay resetting data to allow modal fade out
    setTimeout(() => {
        setTaskToEdit(null);
        // setEditError(null);
    }, 300);
  }, []);

  const handleTaskUpdated = (updatedTask: Task) => {
    setTasks(currentTasks =>
      currentTasks.map(task =>
        task.id === updatedTask.id ? updatedTask : task
      )
    );
    // Optionally close modal here if not handled in form,
    // but TaskForm currently closes itself on success.
  };

  const filteredAndSortedTasks = useMemo(() => {
    let filtered = tasks;

    // Text Search Filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(task => {
        // Search in task description
        if (task.description.toLowerCase().includes(query)) return true;

        // Search in project name
        if (task.project?.name.toLowerCase().includes(query)) return true;

        // Search in business name
        if (task.business?.name.toLowerCase().includes(query)) return true;

        // Search in category names
        if (task.categories.some(cat => cat.name.toLowerCase().includes(query))) return true;

        return false;
      });
    }

    // Status Filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(task => !task.is_done);
    } else if (statusFilter === 'completed') {
      filtered = filtered.filter(task => task.is_done);
    }

    // Category Filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(task =>
        task.categories.some(cat => cat.id === parseInt(categoryFilter))
      );
    }

    // Project Filter
    if (projectFilter !== 'all') {
      filtered = filtered.filter(task => task.project?.id === parseInt(projectFilter));
    }

    // Business Filter
    if (businessFilter !== 'all') {
      filtered = filtered.filter(task => task.business?.id === parseInt(businessFilter));
    }

    // Top Priority Filter
    if (showTopPriority) {
      filtered = filtered.filter(task => task.top_priority);
    }

    // To Plan Filter (tasks without due dates) - takes priority over due date filter
    if (showToPlan) {
      filtered = filtered.filter(task => !task.due_date);
    }
    // Due Date Filter (only apply if "To Plan" is not active)
    else if (showDueTodayOrBefore) {
      const now = new Date();
      const nowUtcYear = now.getUTCFullYear();
      const nowUtcMonth = now.getUTCMonth();
      const nowUtcDay = now.getUTCDate();

      filtered = filtered.filter(task => {
        if (!task.due_date) return false;

        let dueDate: Date;
        try {
          dueDate = parseISO(task.due_date);
          if (isNaN(dueDate.getTime())) return false; // Invalid date string
        } catch (e) {
          return false; // Error parsing date
        }

        // Show tasks due today or before (including overdue)
        const endOfTodayUTC = new Date(Date.UTC(nowUtcYear, nowUtcMonth, nowUtcDay, 23, 59, 59, 999));
        return dueDate.getTime() <= endOfTodayUTC.getTime();
      });
    }

    // Sorting
    filtered.sort((a, b) => {
        let valA: any;
        let valB: any;

        // Handle null/undefined dates/names for sorting
        switch (sortBy) {
            case 'due_date':
                valA = a.due_date ? parseISO(a.due_date).getTime() : (sortDirection === 'asc' ? Infinity : -Infinity);
                valB = b.due_date ? parseISO(b.due_date).getTime() : (sortDirection === 'asc' ? Infinity : -Infinity);
                break;
            case 'created_at':
                valA = a.created_at ? parseISO(a.created_at).getTime() : (sortDirection === 'asc' ? Infinity : -Infinity);
                valB = b.created_at ? parseISO(b.created_at).getTime() : (sortDirection === 'asc' ? Infinity : -Infinity);
                break;
            case 'business':
                // Case-insensitive sort, place tasks without business consistently
                valA = a.business?.name?.toLowerCase() ?? (sortDirection === 'asc' ? '\uffff' : ''); // Sort nulls last ascending
                valB = b.business?.name?.toLowerCase() ?? (sortDirection === 'asc' ? '\uffff' : '');
                break;
            default:
                 return 0; // Should not happen
        }

        if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });


    return filtered;
  }, [tasks, searchQuery, statusFilter, categoryFilter, projectFilter, businessFilter, showDueTodayOrBefore, showToPlan, showTopPriority, sortBy, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (field: SortField) => {
    const isHovered = hoveredSortField === field;
    if (sortBy !== field) {
      return (
        <ChevronDownIcon
          style={{
            width: '12px',
            height: '12px',
            color: COLORS.text.tertiary,
            marginLeft: SPACING[4],
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 150ms'
          }}
        />
      );
    }
    return sortDirection === 'asc' ?
      <ChevronUpIcon style={{ width: '12px', height: '12px', color: COLORS.text.secondary, marginLeft: SPACING[4] }} /> :
      <ChevronDownIcon style={{ width: '12px', height: '12px', color: COLORS.text.secondary, marginLeft: SPACING[4] }} />;
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'To plan';
    try {
      const date = parseISO(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';

      if (dateFnsIsToday(date)) {
        return 'Today';
      } else if (isPast(date) && !dateFnsIsToday(date)) {
        return `${format(date, 'MMM d')} (overdue)`;
      } else {
        return format(date, 'MMM d, yyyy');
      }
    } catch (e) {
      console.error("Error formatting date:", e);
      return 'Invalid date';
    }
  };

  const handleInlineDateChange = async (taskId: number, newDateValue: string) => {
    setIsUpdatingDate(true);
    const originalTasks = [...tasks]; // For potential revert
    const taskToUpdate = originalTasks.find(t => t.id === taskId);
    if (!taskToUpdate) return;

    let newDueDateISO: string | null = null;
    try {
        if (newDateValue) { // newDateValue is "YYYY-MM-DD"
            // Use a simple approach - create date in local timezone
            const [year, month, day] = newDateValue.split('-').map(Number);
            const date = new Date(year, month - 1, day); // month is 0-indexed
            if (!isNaN(date.getTime())) {
                 newDueDateISO = date.toISOString(); // Keep ISO for backend
            } else {
                 throw new Error("Invalid date format selected");
            }
        } // If newDateValue is empty, newDueDateISO remains null

        // Optimistically update UI before API call for responsiveness
        setTasks(currentTasks =>
            currentTasks.map(task =>
                task.id === taskId ? { ...task, due_date: newDueDateISO } : task
            )
        );
        setEditingDateTaskId(null); // Exit edit mode immediately

        // API call - send the correct format required by the backend (YYYY-MM-DD or null)
        const payloadDueDate = newDateValue ? newDateValue : null;
        await apiClient.patch(`/api/tasks/${taskId}/`, {
          due_date: payloadDueDate
        });

    } catch (error: any) {
        console.error("Failed to update due date:", error);
        // Revert UI on error
        setTasks(originalTasks);
        alert(`Error updating due date: ${error.message || 'Failed to update due date.'}`);
    } finally {
        setIsUpdatingDate(false);
    }
  };

  // Focus input when edit mode starts for a task date
  useEffect(() => {
    if (editingDateTaskId !== null && dateInputRef.current) {
      dateInputRef.current.focus();
      // Select the text for easier replacement (optional)
      // dateInputRef.current.select();
    }
  }, [editingDateTaskId]);
  // --- End Inline Date Update Logic ---

  // --- Inline Project Update Logic ---
  const handleInlineProjectChange = async (taskId: number, newProjectId: string) => {
    setIsUpdatingProject(true);
    const originalTasks = [...tasks];
    const taskToUpdate = originalTasks.find(t => t.id === taskId);
    if (!taskToUpdate) return;

    try {
      const projectIdNum = newProjectId ? parseInt(newProjectId) : null;
      const selectedProject = projectIdNum ? projects.find(p => p.id === projectIdNum) : null;

      // Prepare update payload
      const payload: any = {
        project_id: projectIdNum
      };

      // Auto-update business if project has a business
      if (selectedProject?.business) {
        payload.business_id = selectedProject.business.id;
      }

      // Optimistically update UI
      setTasks(currentTasks =>
        currentTasks.map(task => {
          if (task.id === taskId) {
            return {
              ...task,
              project: selectedProject ? { id: selectedProject.id, name: selectedProject.name } : null,
              business: selectedProject?.business ? { id: selectedProject.business.id, name: selectedProject.business.name } : task.business
            };
          }
          return task;
        })
      );
      setEditingProjectTaskId(null);

      // API call
      await apiClient.patch(`/api/tasks/${taskId}/`, payload);
    } catch (error: any) {
      console.error("Failed to update project:", error);
      setTasks(originalTasks);
      alert(`Error updating project: ${error.message || 'Failed to update project.'}`);
    } finally {
      setIsUpdatingProject(false);
    }
  };

  useEffect(() => {
    if (editingProjectTaskId !== null && projectSelectRef.current) {
      projectSelectRef.current.focus();
    }
  }, [editingProjectTaskId]);
  // --- End Inline Project Update Logic ---

  // --- Inline Business Update Logic ---
  const handleInlineBusinessChange = async (taskId: number, newBusinessId: string) => {
    setIsUpdatingBusiness(true);
    const originalTasks = [...tasks];
    const taskToUpdate = originalTasks.find(t => t.id === taskId);
    if (!taskToUpdate) return;

    try {
      const businessIdNum = newBusinessId ? parseInt(newBusinessId) : null;
      const selectedBusiness = businessIdNum ? businesses.find(b => b.id === businessIdNum) : null;


      // Optimistically update UI
      setTasks(currentTasks =>
        currentTasks.map(task => {
          if (task.id === taskId) {
            return {
              ...task,
              business: selectedBusiness ? { id: selectedBusiness.id, name: selectedBusiness.name } : null
            };
          }
          return task;
        })
      );
      setEditingBusinessTaskId(null);

      // API call - use business_id instead of business
      const payload = { business_id: businessIdNum };

      const response = await apiClient.patch(`/api/tasks/${taskId}/`, payload);

    } catch (error: any) {
      console.error("[ERROR] Failed to update business:", error);
      console.error("[ERROR] Error response:", error.response?.data);
      setTasks(originalTasks);
      alert(`Error updating business: ${error.response?.data?.error || error.message || 'Failed to update business.'}`);
    } finally {
      setIsUpdatingBusiness(false);
    }
  };

  useEffect(() => {
    if (editingBusinessTaskId !== null && businessSelectRef.current) {
      businessSelectRef.current.focus();
    }
  }, [editingBusinessTaskId]);
  // --- End Inline Business Update Logic ---

  // --- Inline Categories Update Logic ---
  const startEditingCategories = (taskId: number, currentCategories: Category[]) => {
    setEditingCategoriesTaskId(taskId);
    setTempCategories(currentCategories.map(c => c.id));
  };

  const toggleCategory = (categoryId: number) => {
    setTempCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleInlineCategoriesSubmit = async (taskId: number) => {
    setIsUpdatingCategories(true);
    const originalTasks = [...tasks];

    try {
      // Optimistically update UI
      const updatedCategories = categories.filter(c => tempCategories.includes(c.id));
      setTasks(currentTasks =>
        currentTasks.map(task =>
          task.id === taskId ? { ...task, categories: updatedCategories } : task
        )
      );
      setEditingCategoriesTaskId(null);

      // API call - use category_ids instead of categories
      await apiClient.patch(`/api/tasks/${taskId}/`, {
        category_ids: tempCategories
      });
    } catch (error: any) {
      console.error("Failed to update categories:", error);
      setTasks(originalTasks);
      alert(`Error updating categories: ${error.message || 'Failed to update categories.'}`);
    } finally {
      setIsUpdatingCategories(false);
    }
  };

  const cancelCategoriesEdit = () => {
    setEditingCategoriesTaskId(null);
    setTempCategories([]);
  };
  // --- End Inline Categories Update Logic ---

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: SPACING[24] }}>
        <Spinner size="md" />
      </div>
    );
  }

  // Common styles
  const selectStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    paddingLeft: SPACING[12],
    paddingRight: SPACING[32],
    paddingTop: SPACING[4],
    paddingBottom: SPACING[4],
    border: `1px solid ${COLORS.border.default}`,
    borderRadius: RADIUS,
    fontSize: TYPOGRAPHY.body.sizes.small,
    outline: 'none'
  };

  const checkboxStyle: React.CSSProperties = {
    width: '16px',
    height: '16px',
    borderRadius: RADIUS,
    border: `1px solid ${COLORS.border.default}`,
    cursor: 'pointer'
  };

  const tableHeaderCellStyle: React.CSSProperties = {
    padding: `${SPACING[8]} ${SPACING[12]}`,
    textAlign: 'left',
    fontSize: TYPOGRAPHY.body.sizes.tiny,
    fontWeight: TYPOGRAPHY.weight.medium,
    color: COLORS.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  };

  const tableCellStyle: React.CSSProperties = {
    padding: `${SPACING[4]} ${SPACING[12]}`,
    fontSize: TYPOGRAPHY.body.sizes.small,
    color: COLORS.text.secondary,
    verticalAlign: 'top'
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      {error && (
        <Alert variant="error">
          {error}
        </Alert>
      )}

      {/* Search Bar - always at the top */}
      <div style={{ position: 'relative', maxWidth: '28rem', marginBottom: SPACING[8] }}>
        <div style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          paddingLeft: SPACING[12],
          display: 'flex',
          alignItems: 'center',
          pointerEvents: 'none'
        }}>
          <MagnifyingGlassIcon style={{ width: '16px', height: '16px', color: COLORS.text.tertiary }} />
        </div>
        <input
          type="text"
          placeholder="Search tasks, projects, businesses, or categories..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            display: 'block',
            width: '100%',
            paddingLeft: SPACING[40],
            paddingRight: SPACING[12],
            paddingTop: SPACING[8],
            paddingBottom: SPACING[8],
            border: `1px solid ${COLORS.border.default}`,
            borderRadius: RADIUS,
            lineHeight: 1.25,
            backgroundColor: COLORS.bg.panel,
            fontSize: TYPOGRAPHY.body.sizes.small,
            outline: 'none'
          }}
        />
      </div>

      {/* Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: SPACING[12],
        fontSize: TYPOGRAPHY.body.sizes.small
      }}>
        {/* Status Filter */}
        <div>
          <label htmlFor="status-filter" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}>Status</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            style={selectStyle}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        {/* Category Filter */}
         <div>
           <label htmlFor="category-filter" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}>Category</label>
           <select
             id="category-filter"
             value={categoryFilter}
             onChange={(e) => setCategoryFilter(e.target.value)}
             style={selectStyle}
           >
             <option value="all">All Categories</option>
             {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
           </select>
         </div>

        {/* Project Filter */}
        <div>
          <label htmlFor="project-filter" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}>Project</label>
          <select
            id="project-filter"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="all">All Projects</option>
            {projects.map(proj => <option key={proj.id} value={proj.id}>{proj.name}</option>)}
          </select>
        </div>

        {/* Business Filter */}
        <div>
          <label htmlFor="business-filter" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}>Business</label>
          <select
            id="business-filter"
            value={businessFilter}
            onChange={(e) => setBusinessFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="all">All Businesses</option>
            {businesses.map(biz => <option key={biz.id} value={biz.id}>{biz.name}</option>)}
          </select>
        </div>

        {/* Quick Filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[16], flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input
              id="top-priority-filter"
              name="top-priority-filter"
              type="checkbox"
              style={checkboxStyle}
              checked={showTopPriority}
              onChange={(e) => setShowTopPriority(e.target.checked)}
            />
            <label htmlFor="top-priority-filter" style={{ marginLeft: SPACING[8], display: 'block', fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.primary, fontWeight: showTopPriority ? '600' : '400' }}>
              Top Priority
            </label>
          </div>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input
              id="due-today-or-before-filter"
              name="due-today-or-before-filter"
              type="checkbox"
              style={checkboxStyle}
              checked={showDueTodayOrBefore}
              onChange={(e) => {
                setShowDueTodayOrBefore(e.target.checked);
                if (e.target.checked) setShowToPlan(false); // Uncheck "To Plan" if checking this
              }}
            />
            <label htmlFor="due-today-or-before-filter" style={{ marginLeft: SPACING[8], display: 'block', fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.primary }}>
              Due today or before
            </label>
          </div>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input
              id="to-plan-filter"
              name="to-plan-filter"
              type="checkbox"
              style={checkboxStyle}
              checked={showToPlan}
              onChange={(e) => {
                setShowToPlan(e.target.checked);
                if (e.target.checked) setShowDueTodayOrBefore(false); // Uncheck "Due today or before" if checking this
              }}
            />
            <label htmlFor="to-plan-filter" style={{ marginLeft: SPACING[8], display: 'block', fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.primary }}>
              To plan only
            </label>
          </div>
        </div>
      </div>

      {/* Task List */}
      <div style={{ overflowX: 'auto', border: `1px solid ${COLORS.border.default}`, borderRadius: RADIUS }}>
        <table style={{ minWidth: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ backgroundColor: COLORS.bg.base }}>
            <tr>
              <th scope="col" style={{ ...tableHeaderCellStyle, width: '40px' }}> {/* Checkbox */} </th>
              <th scope="col" style={tableHeaderCellStyle}>Task</th>
              <th
                scope="col"
                style={{ ...tableHeaderCellStyle, cursor: 'pointer', width: '96px' }}
                onClick={() => handleSort('due_date')}
                onMouseEnter={() => setHoveredSortField('due_date')}
                onMouseLeave={() => setHoveredSortField(null)}
              >
                 <span style={{ display: 'flex', alignItems: 'center' }}>Due {renderSortIcon('due_date')}</span>
              </th>
              <th scope="col" style={{ ...tableHeaderCellStyle, width: '160px' }}>Project</th>
              <th
                scope="col"
                style={{ ...tableHeaderCellStyle, cursor: 'pointer', width: '128px' }}
                onClick={() => handleSort('business')}
                onMouseEnter={() => setHoveredSortField('business')}
                onMouseLeave={() => setHoveredSortField(null)}
              >
                 <span style={{ display: 'flex', alignItems: 'center' }}>Business {renderSortIcon('business')}</span>
              </th>
              <th scope="col" style={{ ...tableHeaderCellStyle, width: '160px' }}>Categories</th>
              <th
                scope="col"
                style={{ ...tableHeaderCellStyle, cursor: 'pointer', width: '112px' }}
                onClick={() => handleSort('created_at')}
                onMouseEnter={() => setHoveredSortField('created_at')}
                onMouseLeave={() => setHoveredSortField(null)}
              >
                 <span style={{ display: 'flex', alignItems: 'center' }}>Created {renderSortIcon('created_at')}</span>
              </th>
              <th scope="col" style={{ ...tableHeaderCellStyle, textAlign: 'center', width: '64px' }}>Actions</th>
            </tr>
          </thead>
          <tbody style={{ backgroundColor: COLORS.bg.panel }}>
            {filteredAndSortedTasks.length > 0 ? (
              filteredAndSortedTasks.map((task) => (
                <tr
                  key={task.id}
                  style={{
                    borderBottom: `1px solid ${COLORS.border.default}`,
                    backgroundColor: hoveredRowId === task.id ? COLORS.bg.hover : 'transparent',
                    transition: 'background-color 100ms',
                    opacity: task.is_done ? 0.6 : 1
                  }}
                  onMouseEnter={() => setHoveredRowId(task.id)}
                  onMouseLeave={() => setHoveredRowId(null)}
                >
                  {/* Checkbox Column */}
                  <td style={{ ...tableCellStyle, whiteSpace: 'nowrap' }}>
                    <input
                      type="checkbox"
                      style={{
                        ...checkboxStyle,
                        opacity: isToggling && taskBeingToggledId === task.id ? 0.5 : 1,
                        cursor: isToggling && taskBeingToggledId === task.id ? 'not-allowed' : 'pointer'
                      }}
                      checked={task.is_done}
                      onChange={() => handleToggleDone(task.id)}
                      disabled={isToggling && taskBeingToggledId === task.id}
                    />
                  </td>
                  {/* Task Description Column */}
                  <td style={{ ...tableCellStyle, color: COLORS.text.primary }}>
                      <span style={{ textDecoration: task.is_done ? 'line-through' : 'none', color: task.is_done ? COLORS.text.secondary : COLORS.text.primary }}>{task.description}</span>
                      {task.is_done && task.completion_date && (
                          <span style={{ display: 'block', fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary, marginTop: '2px' }}>
                            Completed: {format(parseISO(task.completion_date), 'MMM d, yyyy')}
                          </span>
                      )}
                  </td>
                  {/* Due Date Column */}
                  <td style={{ ...tableCellStyle, whiteSpace: 'nowrap' }}>
                    {editingDateTaskId === task.id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[4] }}>
                        <input
                          ref={dateInputRef}
                          type="date"
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '2px',
                            border: `1px solid ${COLORS.accent.default}`,
                            borderRadius: RADIUS,
                            fontSize: TYPOGRAPHY.body.sizes.tiny,
                            outline: 'none',
                            opacity: isUpdatingDate ? 0.5 : 1
                          }}
                          value={formatISOForInput(task.due_date)}
                          onChange={(e) => handleInlineDateChange(task.id, e.target.value)}
                          onBlur={(e) => {
                            // Don't close if clicking the clear button
                            if (!e.relatedTarget?.classList.contains('clear-date-btn')) {
                              setEditingDateTaskId(null);
                            }
                          }}
                          disabled={isUpdatingDate}
                        />
                        <button
                          type="button"
                          className="clear-date-btn"
                          style={{
                            padding: `2px ${SPACING[8]}`,
                            fontSize: TYPOGRAPHY.body.sizes.tiny,
                            color: COLORS.status.error,
                            borderRadius: RADIUS,
                            border: `1px solid ${COLORS.status.error}`,
                            backgroundColor: 'transparent',
                            whiteSpace: 'nowrap',
                            opacity: isUpdatingDate ? 0.5 : 1,
                            cursor: isUpdatingDate ? 'not-allowed' : 'pointer'
                          }}
                          onClick={() => handleInlineDateChange(task.id, '')}
                          onMouseDown={(e) => e.preventDefault()} // Prevent blur on button click
                          disabled={isUpdatingDate}
                          title="Clear due date (mark as 'to plan')"
                        >
                          Clear
                        </button>
                      </div>
                    ) : (
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: task.is_done ? 'default' : 'pointer'
                        }}
                        onClick={() => { if (!task.is_done) setEditingDateTaskId(task.id); }}
                        title={task.is_done ? "Cannot edit date for completed task" : "Click to edit due date"}
                      >
                          <CalendarDaysIcon style={{
                            width: '16px',
                            height: '16px',
                            marginRight: SPACING[4],
                            color: task.due_date && isPast(parseISO(task.due_date)) && !dateFnsIsToday(parseISO(task.due_date)) && !task.is_done
                              ? COLORS.status.error
                              : COLORS.text.tertiary
                          }} />
                          <span>{formatDate(task.due_date)}</span>
                      </span>
                    )}
                  </td>
                  {/* Project Column */}
                  <td style={tableCellStyle}>
                    {editingProjectTaskId === task.id ? (
                      <select
                        ref={projectSelectRef}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '2px',
                          border: `1px solid ${COLORS.accent.default}`,
                          borderRadius: RADIUS,
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          outline: 'none',
                          opacity: isUpdatingProject ? 0.5 : 1
                        }}
                        value={task.project?.id || ''}
                        onChange={(e) => handleInlineProjectChange(task.id, e.target.value)}
                        onBlur={() => setEditingProjectTaskId(null)}
                        disabled={isUpdatingProject}
                      >
                        <option value="">No project</option>
                        {projects.map(project => (
                          <option key={project.id} value={project.id}>
                            {project.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          cursor: task.is_done ? 'default' : 'pointer',
                          minHeight: '20px'
                        }}
                        onClick={() => { if (!task.is_done) setEditingProjectTaskId(task.id); }}
                        title={task.is_done ? "Cannot edit project for completed task" : "Click to edit project"}
                      >
                        {task.project ? (
                          <>
                            <FolderIcon style={{ width: '14px', height: '14px', marginRight: SPACING[4], flexShrink: 0, color: COLORS.text.tertiary }} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={task.project.name}>{task.project.name}</span>
                          </>
                        ) : (
                          <span style={{ color: COLORS.text.tertiary, fontStyle: 'italic' }}>No project</span>
                        )}
                      </div>
                    )}
                  </td>
                  {/* Business Column */}
                  <td style={tableCellStyle}>
                    {editingBusinessTaskId === task.id ? (
                      <select
                        ref={businessSelectRef}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '2px',
                          border: `1px solid ${COLORS.accent.default}`,
                          borderRadius: RADIUS,
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          outline: 'none',
                          opacity: isUpdatingBusiness ? 0.5 : 1
                        }}
                        value={task.business?.id || ''}
                        onChange={(e) => handleInlineBusinessChange(task.id, e.target.value)}
                        onBlur={() => setEditingBusinessTaskId(null)}
                        disabled={isUpdatingBusiness}
                      >
                        <option value="">No business</option>
                        {businesses.map(business => (
                          <option key={business.id} value={business.id}>
                            {business.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: TYPOGRAPHY.body.sizes.tiny,
                          cursor: task.is_done ? 'default' : 'pointer',
                          minHeight: '20px'
                        }}
                        onClick={() => { if (!task.is_done) setEditingBusinessTaskId(task.id); }}
                        title={task.is_done ? "Cannot edit business for completed task" : "Click to edit business"}
                      >
                        {task.business ? (
                          <>
                            <BriefcaseIcon style={{ width: '16px', height: '16px', marginRight: SPACING[4], flexShrink: 0, color: COLORS.text.tertiary }} />
                            <span>{task.business.name}</span>
                          </>
                        ) : (
                          <span style={{ color: COLORS.text.tertiary, fontStyle: 'italic' }}>No business</span>
                        )}
                      </div>
                    )}
                  </td>
                  {/* Categories Column */}
                  <td style={tableCellStyle}>
                    {editingCategoriesTaskId === task.id ? (
                      <div style={{ position: 'relative', border: `1px solid ${COLORS.accent.default}`, borderRadius: RADIUS, padding: SPACING[8], backgroundColor: COLORS.bg.panel, minWidth: '200px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[4], maxHeight: '160px', overflowY: 'auto' }}>
                          {categories.map(category => (
                            <label key={category.id} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: SPACING[4], borderRadius: RADIUS }}>
                              <input
                                type="checkbox"
                                checked={tempCategories.includes(category.id)}
                                onChange={() => toggleCategory(category.id)}
                                style={{ width: '12px', height: '12px', borderRadius: RADIUS, border: `1px solid ${COLORS.border.default}`, marginRight: SPACING[8] }}
                              />
                              <span style={{ fontSize: TYPOGRAPHY.body.sizes.tiny }}>{category.name}</span>
                            </label>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: SPACING[4], marginTop: SPACING[8], paddingTop: SPACING[8], borderTop: `1px solid ${COLORS.border.default}` }}>
                          <button
                            onClick={() => handleInlineCategoriesSubmit(task.id)}
                            disabled={isUpdatingCategories}
                            style={{
                              padding: `${SPACING[4]} ${SPACING[8]}`,
                              fontSize: TYPOGRAPHY.body.sizes.tiny,
                              backgroundColor: COLORS.accent.default,
                              color: '#FFFFFF',
                              borderRadius: RADIUS,
                              border: 'none',
                              cursor: isUpdatingCategories ? 'not-allowed' : 'pointer',
                              opacity: isUpdatingCategories ? 0.5 : 1
                            }}
                          >
                            {isUpdatingCategories ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={cancelCategoriesEdit}
                            disabled={isUpdatingCategories}
                            style={{
                              padding: `${SPACING[4]} ${SPACING[8]}`,
                              fontSize: TYPOGRAPHY.body.sizes.tiny,
                              backgroundColor: COLORS.bg.hover,
                              color: COLORS.text.primary,
                              borderRadius: RADIUS,
                              border: 'none',
                              cursor: isUpdatingCategories ? 'not-allowed' : 'pointer',
                              opacity: isUpdatingCategories ? 0.5 : 1
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{ cursor: task.is_done ? 'default' : 'pointer', minHeight: '20px' }}
                        onClick={() => { if (!task.is_done) startEditingCategories(task.id, task.categories); }}
                        title={task.is_done ? "Cannot edit categories for completed task" : "Click to edit categories"}
                      >
                        {task.categories.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING[4] }}>
                            {task.categories.map(cat => (
                              <Badge key={cat.id} variant="default" size="sm" style={{ borderRadius: RADIUS }}>
                                <TagIcon style={{ width: '12px', height: '12px', marginRight: '2px' }} />
                                {cat.name}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: COLORS.text.tertiary, fontStyle: 'italic', fontSize: TYPOGRAPHY.body.sizes.tiny }}>No categories</span>
                        )}
                      </div>
                    )}
                  </td>
                   {/* Created Column */}
                   <td style={{ ...tableCellStyle, whiteSpace: 'nowrap' }}>
                     {task.created_at ? format(parseISO(task.created_at), 'MMM d, yyyy') : 'N/A'}
                  </td>
                  {/* Actions Column */}
                  <td style={{ ...tableCellStyle, whiteSpace: 'nowrap', textAlign: 'center', fontWeight: TYPOGRAPHY.weight.medium }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: SPACING[4] }}>
                      {/* Toggle Done Button */}
                      <Button
                        onClick={() => handleToggleDone(task.id)}
                        variant="ghost"
                        size="sm"
                        style={{
                          padding: SPACING[4],
                          height: 'auto',
                          color: task.is_done ? COLORS.status.warning : COLORS.status.success
                        }}
                        aria-label={task.is_done ? "Mark task as active" : "Mark task as complete"}
                        title={task.is_done ? "Mark task as active" : "Mark task as complete"}
                        disabled={isToggling && taskBeingToggledId === task.id}
                      >
                         {isToggling && taskBeingToggledId === task.id ? (
                            <Spinner size="sm" />
                         ) : task.is_done ? (
                            <XCircleIcon style={{ width: '16px', height: '16px' }} />
                         ) : (
                            <CheckCircleIcon style={{ width: '16px', height: '16px' }} />
                         )}
                      </Button>

                      {/* Edit Button */}
                      <Button
                        onClick={() => openEditModal(task)}
                        variant="ghost"
                        size="sm"
                        style={{
                          padding: SPACING[4],
                          height: 'auto',
                          color: COLORS.accent.default
                        }}
                        aria-label={`Edit task ${task.description}`}
                        title="Edit task"
                        disabled={task.is_done}
                      >
                        <PencilSquareIcon style={{ width: '16px', height: '16px' }} />
                      </Button>

                      {/* Delete Button */}
                      <Button
                        onClick={() => openConfirmationModal(task.id)}
                        variant="ghost"
                        size="sm"
                        style={{
                          padding: SPACING[4],
                          height: 'auto',
                          color: COLORS.status.error
                        }}
                        aria-label={`Delete task ${task.description}`}
                        disabled={task.is_done}
                        title={task.is_done ? "Cannot delete completed tasks" : "Delete task"}
                      >
                        <TrashIcon style={{ width: '16px', height: '16px' }} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: `${SPACING[16]} ${SPACING[24]}`, fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary }}>
                  No tasks match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      <Transition appear show={isConfirmModalOpen} as={Fragment}>
        <Dialog as="div" style={{ position: 'relative', zIndex: 50 }} onClose={closeConfirmationModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, backgroundColor: 'rgba(0, 0, 0, 0.3)' }} />
          </Transition.Child>

          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, overflowY: 'auto' }}>
            <div style={{ display: 'flex', minHeight: '100%', alignItems: 'center', justifyContent: 'center', padding: SPACING[16], textAlign: 'center' }}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-150" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                leave="ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel style={{
                  width: '100%',
                  maxWidth: '28rem',
                  overflow: 'hidden',
                  borderRadius: RADIUS,
                  backgroundColor: COLORS.bg.panel,
                  padding: SPACING[24],
                  textAlign: 'left',
                  verticalAlign: 'middle',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                }}>
                  <Dialog.Title as="h3" style={{
                    fontSize: TYPOGRAPHY.headings.sizes.h2,
                    fontWeight: TYPOGRAPHY.weight.semibold,
                    lineHeight: 1.5,
                    color: COLORS.text.primary,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <ExclamationTriangleIcon style={{ width: '24px', height: '24px', color: COLORS.status.error, marginRight: SPACING[8] }} />
                    Confirm Deletion
                  </Dialog.Title>
                  <div style={{ marginTop: SPACING[12] }}>
                    <p style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary }}>
                      Are you sure you want to delete this task? This action cannot be undone.
                    </p>
                  </div>

                  {deleteError && (
                    <div style={{ marginTop: SPACING[16] }}>
                      <Alert variant="error">
                        {deleteError}
                      </Alert>
                    </div>
                  )}

                  <div style={{
                    marginTop: SPACING[24],
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: SPACING[12]
                  }}>
                    <Button
                      type="button"
                      variant="danger"
                      fullWidth
                      style={{ gridColumn: '2' }}
                      onClick={handleDeleteTask}
                      disabled={isDeleting}
                      isLoading={isDeleting}
                      loadingText="Deleting..."
                    >
                      Confirm Delete
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      fullWidth
                      style={{ gridColumn: '1', gridRow: '1' }}
                      onClick={closeConfirmationModal}
                      disabled={isDeleting}
                    >
                      Cancel
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Edit Task Modal - Uncommented and props added */}
      {taskToEdit && (
          <TaskForm
             isOpen={isEditModalOpen}
             onClose={closeEditModal}
             // Pass the update handler instead of creation handler for edit mode
             onTaskCreated={() => {}} // Keep this for prop type conformity, or adjust TaskForm props
             onTaskUpdated={handleTaskUpdated} // Pass the new update handler
             initialTaskData={taskToEdit} // Pass task data to form
             isEditMode={true}          // Indicate form is in edit mode
          />
      )}
    </div>
  );
};

export default TaskList;
